import { Alert, Card, Empty, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import { Pair } from 'types/pair';
import { useBrowserTabs } from '../../components/BrowserTabs';
import { useSettingsStore } from '../../components/Settings/hooks/useSettingsStore';
import { useAuth } from '../../contexts/AuthContext';
import { configService } from '../../services/configService';
import { hasValidLicense } from '../../utils/licenseUtils';
import { buildTerminalUrl } from '../../utils/terminalUrls';
import HeaderBar from './components/HeaderBar';
import PairCardList from './components/PairCardList';
import { usePairRecords } from './hooks/usePairRecords';
import './PairList.css';
import { isFilterActuallyEnabled } from './utils/filterUtils';

const LoadingState: React.FC = () => (
  <Card>
    <div className="axiom-pair-loading">
      <Spin size="large" />
      <div className="axiom-pair-loading__text">Loading pulse records...</div>
    </div>
  </Card>
);

const PairList: React.FC = () => {
  const [isHoveringPair, setIsHoveringPair] = React.useState(false);
  const {
    filteredRecords,
    pairRecords,
    loading,
    error,
    fetchPairRecords,
    newlyAddedIds,
    isPaused,
    setIsPaused,
    clearPairRecords,
  } = usePairRecords(isHoveringPair);
  const { count, openTab, openedPairIds, markPairAsOpened, clearOpenedPairs } = useBrowserTabs();
  const { general, setGeneral, filters, lists } = useSettingsStore();
  const { user } = useAuth();
  const [licenseEnabled, setLicenseEnabled] = useState<boolean>(true); // Default to true to avoid flicker

  // Fetch license enabled setting from API
  useEffect(() => {
    configService.getPublicConfig().then(config => {
      setLicenseEnabled(config.licenseEnabled);
    });
  }, []);

  // Check license status from JWT token
  const userHasValidLicense = useMemo(() => {
    return hasValidLicense(user?.licenseExpiresAt);
  }, [user?.licenseExpiresAt]);

  // Normalize whitelist addresses for quick lookup
  const devWhiteList = useMemo(() => {
    if (!lists.devWhiteList) {
      return new Set<string>();
    }
    return new Set(
      lists.devWhiteList
        .split(/\r?\n/)
        .map((line: string) => line.trim().toLowerCase())
        .filter(Boolean)
    );
  }, [lists.devWhiteList]);

  // Clear opened pairs tracking when auto tab opening is disabled
  useEffect(() => {
    if (!general.autoTabOpeningEnabled) {
      clearOpenedPairs();
    }
  }, [general.autoTabOpeningEnabled, clearOpenedPairs]);

  // Auto tab opening logic
  useEffect(() => {
    // Only proceed if at least one auto-open option is enabled
    if ((!general.autoTabOpeningEnabled && !general.autoOpeningWhitelistOnly) || newlyAddedIds.size === 0) return;
    if (count >= general.maxTabs) return;

    // Helper function to check if a pair is whitelisted
    const isWhitelisted = (pair: Pair) => {
      const normalizedDevAddress = pair.devAddress?.trim().toLowerCase();
      const normalizedTwitterUsername = pair.twitterUsername?.trim().toLowerCase();
      return (normalizedDevAddress && devWhiteList.has(normalizedDevAddress)) ||
        (normalizedTwitterUsername && devWhiteList.has(normalizedTwitterUsername));
    };

    // Collect pairs to open using a Set to avoid duplicates
    const pairsToOpenSet = new Set<Pair>();

    // If "auto open filtered" is enabled, add pairs from filteredRecords
    if (general.autoTabOpeningEnabled) {
      const filteredCandidates = filteredRecords.filter(pair =>
        newlyAddedIds.has(pair.id) &&
        !openedPairIds.has(pair.id)
      );
      filteredCandidates.forEach(pair => pairsToOpenSet.add(pair));
    }

    // If "auto open whitelisted" is enabled, check ALL records (before filters)
    // This ensures whitelisted pairs are opened independently of filters
    if (general.autoOpeningWhitelistOnly) {
      const whitelistedCandidates = pairRecords.filter(pair =>
        newlyAddedIds.has(pair.id) &&
        !openedPairIds.has(pair.id) &&
        isWhitelisted(pair)
      );
      whitelistedCandidates.forEach(pair => pairsToOpenSet.add(pair));
    }

    // Convert set to array and limit by max tabs
    const pairsToOpen = Array.from(pairsToOpenSet).slice(0, general.maxTabs - count);

    // Open tabs for new pairs and mark them as opened
    pairsToOpen.forEach(pair => {
      const url = buildTerminalUrl(general.terminal, pair.tokenAddress, pair.pairAddress);
      openTab(url);
      markPairAsOpened(pair.id);
    });
  }, [newlyAddedIds, general.autoTabOpeningEnabled, general.autoOpeningWhitelistOnly, general.terminal, count, general.maxTabs, filteredRecords, pairRecords, openTab, openedPairIds, markPairAsOpened, devWhiteList]);

  if (loading && filteredRecords.length === 0) {
    return <LoadingState />;
  }

  const handlePauseToggle = () => {
    setIsPaused(!isPaused);
  };

  const handleAutoTabToggle = () => {
    setGeneral('autoTabOpeningEnabled', !general.autoTabOpeningEnabled);
  };

  const handleAutoOpeningWhitelistToggle = () => {
    setGeneral('autoOpeningWhitelistOnly', !general.autoOpeningWhitelistOnly);
  };

  const getFilterDescription = () => {
    const activeFilters: string[] = [];

    if (general.hideWithoutRelated) {
      activeFilters.push('Hide pairs without related');
    }

    if (isFilterActuallyEnabled(filters.motionPercent)) {
      const from = filters.motionPercent.from.trim() || 'none';
      const to = filters.motionPercent.to.trim() || 'none';
      activeFilters.push(`Motion: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.bmotionPercent)) {
      const from = filters.bmotionPercent.from.trim() || 'none';
      const to = filters.bmotionPercent.to.trim() || 'none';
      activeFilters.push(`BMotion Percent: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.maxMCPercent)) {
      const from = filters.maxMCPercent.from.trim() || 'none';
      const to = filters.maxMCPercent.to.trim() || 'none';
      activeFilters.push(`Max Market Cap Percent: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.openPrice)) {
      const from = filters.openPrice.from.trim() || 'none';
      const to = filters.openPrice.to.trim() || 'none';
      activeFilters.push(`Open Price: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.totalPairFees)) {
      const from = filters.totalPairFees.from.trim() || 'none';
      const to = filters.totalPairFees.to.trim() || 'none';
      activeFilters.push(`Total Pair Fees: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.migrationPercent)) {
      const from = filters.migrationPercent.from.trim() || 'none';
      const to = filters.migrationPercent.to.trim() || 'none';
      activeFilters.push(`Migration %: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.devPairsCount)) {
      const from = filters.devPairsCount.from.trim() || 'none';
      const to = filters.devPairsCount.to.trim() || 'none';
      activeFilters.push(`Dev Pairs Count: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.motionAvgPercent)) {
      const from = filters.motionAvgPercent.from.trim() || 'none';
      const to = filters.motionAvgPercent.to.trim() || 'none';
      activeFilters.push(`Motion Avg %: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.bmotionAvgPercent)) {
      const from = filters.bmotionAvgPercent.from.trim() || 'none';
      const to = filters.bmotionAvgPercent.to.trim() || 'none';
      activeFilters.push(`BMotion Avg %: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.maxMCAvgPercent)) {
      const from = filters.maxMCAvgPercent.from.trim() || 'none';
      const to = filters.maxMCAvgPercent.to.trim() || 'none';
      activeFilters.push(`Max Market Cap Avg %: ${from} - ${to}`);
    }

    if (filters.twitterHasAccount.enabled) {
      activeFilters.push('Has Twitter Account');
    }

    if (filters.twitterVerified.enabled) {
      activeFilters.push('Twitter Verified');
    }

    if (filters.twitterCommunity.enabled) {
      activeFilters.push('Has Twitter Community');
    }

    if (isFilterActuallyEnabled(filters.twitterFollowers)) {
      const from = filters.twitterFollowers.from.trim() || 'none';
      const to = filters.twitterFollowers.to.trim() || 'none';
      activeFilters.push(`Twitter Admin Followers: ${from} - ${to}`);
    }

    if (isFilterActuallyEnabled(filters.twitterFollowing)) {
      const from = filters.twitterFollowing.from.trim() || 'none';
      const to = filters.twitterFollowing.to.trim() || 'none';
      activeFilters.push(`Twitter Admin Following: ${from} - ${to}`);
    }

    if (activeFilters.length === 0) {
      return 'No pairs matched to filter yet';
    }

    return (
      <div style={{ textAlign: 'center' }}>
        <div>No pairs matched to filter yet</div>
        <div style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c', display: 'inline-block', textAlign: 'left' }}>
          Active filters:
          <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 0, listStylePosition: 'outside', marginLeft: '1em' }}>
            {activeFilters.map((filter, index) => (
              <li key={index}>{filter}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <HeaderBar
        loading={loading}
        onRefresh={fetchPairRecords}
        isPaused={isPaused}
        onPauseToggle={handlePauseToggle}
        onClear={clearPairRecords}
        autoTabOpeningEnabled={general.autoTabOpeningEnabled}
        onAutoTabToggle={handleAutoTabToggle}
        autoOpeningWhitelistOnly={general.autoOpeningWhitelistOnly}
        onAutoOpeningWhitelistToggle={handleAutoOpeningWhitelistToggle}
        tabCount={count}
        isHoveringPair={isHoveringPair}
      />

      {licenseEnabled && !userHasValidLicense && (
        <Alert
          message={undefined}
          description={
            <div>
              <div>
                <strong>No active license.</strong> You are only receiving every 10th record.
              </div>
              <div>
                To purchase the full plan, please contact support:{' '}
                <a href="https://t.me/FlipScoutSupport" target="_blank" rel="noopener noreferrer">
                  @FlipScoutSupport
                </a>
              </div>
            </div>
          }
          type="info"
          showIcon
          className="axiom-pair-alert"
        />
      )}

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          className="axiom-pair-alert"
        />
      )}

      <div className="axiom-pair-content">
        {filteredRecords.length === 0 ? (
          <Empty description={getFilterDescription()} className="axiom-pair-empty" />
        ) : (
          <PairCardList
            pairs={filteredRecords}
            highlighted={newlyAddedIds}
            onPairHover={setIsHoveringPair}
          />
        )}
      </div>
    </Card>
  );
};

export default PairList;
